import React from 'react';
import css from './CreativeMediaFormFields.module.css';
import { FormattedMessage, useIntl } from 'react-intl';
import TitleInputField from '../TitleInputField';
import PriceInputField from '../PriceInputField';
import DateNeededInputField from '../DateNeededInputField';
import PersonalizationFormField from '../PersonalizationFormField';
import UploadImagesField from '../UploadImagesField/UploadImagesField';
import { ShippingAddressFormFields } from '../../../../components';
import BookingTimeFormField from '../BookingTimeFormField/BookingTimeFormField';

const TITLE_CONFIG = {
  'Product Testing': {
    needsAddress: true,
    needsImages: true,
    needsDescription: true,
    needsScript: false,
    dateLabel: 'Date Product Testing Video is needed',
    imagesLabel: 'Upload product images (to be included in testing video)',
    descriptionLabel: 'Please enter the exact text for your product testing request',
    descriptionPlaceholder: 'e.g. Test this protein powder for 2 weeks and share honest results...',
  },
  'Photo Creation': {
    needsImages: true,
    needsDescription: true,
    dateLabel: 'Date Photo Creation is needed',
    imagesLabel: 'Upload reference images for the photo creation',
    descriptionLabel: 'Please describe your photo creation request',
    descriptionPlaceholder: 'e.g. A cinematic portrait in a dark forest with dramatic lighting...',
  },
  'Video Creation with Audio': {
    needsImages: true,
    needsDescription: true,
    needsScript: true,
    dateLabel: 'Date Video Creation is needed',
    imagesLabel: 'Upload any images to include in the video',
    descriptionLabel: 'Please describe the video request',
    scriptLabel: 'Please enter the exact audio script',
    scriptPlaceholder: 'Type the full script word-for-word...',
  },
  'Audio Creation': {
    needsImages: true,
    needsScript: true,
    dateLabel: 'Date Audio Creation is needed',
    imagesLabel: 'Upload any images to include in the audio creation',
    scriptLabel: 'Please enter the exact script for your audio creation',
    scriptPlaceholder: "e.g. 'Hey everyone, this is [YourName] and today I want to talk about...'",
  },
  'Unboxing Video': {
    needsImages: true,
    needsDescription: true,
    needsScript: true,
    dateLabel: 'Date Unboxing Video is needed',
    imagesLabel: 'Upload images of the product (optional for reference)',
    descriptionLabel: 'Describe anything specific about the unboxing',
    scriptLabel: 'Please enter the exact audio script (or leave blank for natural reaction)',
  },
  'How-To Video': {
    needsImages: true,
    needsDescription: true,
    needsScript: true,
    dateLabel: 'Date How-To Video is needed',
    imagesLabel: 'Upload any reference images or visuals',
    descriptionLabel: 'What should the how-to video teach?',
    scriptLabel: 'Please enter the exact script or key talking points',
  },
  'Voiceover Recording': {
    needsImages: true,
    needsDescription: true,
    needsScript: true,
    dateLabel: 'Date Voiceover Recording is needed',
    imagesLabel: 'Upload visuals (if syncing to video)',
    descriptionLabel: 'Describe the tone and style of voiceover',
    scriptLabel: 'Please enter the exact voiceover script',
  },
  'Podcast Clip': {
    needsImages: true,
    needsDescription: true,
    needsScript: true,
    dateLabel: 'Date Podcast Clip is needed',
    imagesLabel: 'Upload any visuals or branding assets',
    descriptionLabel: 'What topic or moment should be clipped?',
    scriptLabel: 'Provide exact script or talking points',
  },
  'Behind-the-Scenes Video': {
    needsImages: true,
    needsDescription: true,
    needsScript: true,
    dateLabel: 'Date Behind-the-Scenes Video is needed',
    imagesLabel: 'Upload any reference shots or mood board',
    descriptionLabel: 'What should be shown in the BTS video?',
    scriptLabel: 'Any specific lines or narration?',
  },
  'Fitness Demonstration Video': {
    needsImages: true,
    needsDescription: true,
    needsScript: true,
    dateLabel: 'Date Fitness Demo Video is needed',
    imagesLabel: 'Upload exercise images or gym setup',
    descriptionLabel: 'Which exercise(s) should be demonstrated?',
    scriptLabel: 'Provide exact instructions or voiceover script',
  },
  'Custom Articles': {
    needsDescription: true,
    needsDateTimePicker: true, // You'll need a calendar with time slots
    dateLabel: 'Desired Date and Time',
    descriptionLabel: 'Describe the article topic and requirements',
    descriptionPlaceholder: 'e.g. 1000-word article on sustainable fashion trends in 2025...',
  },
};

const CreativeMediaFormFields = ({ formId, values, listing }) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const selectedTitle = values?.title;

  const config = selectedTitle ? TITLE_CONFIG[selectedTitle] : null;

  // Default fallback if no title selected yet
  if (!selectedTitle || !config) {
    return (
      <>
        <div className={css.offeringBlock}>
          <div className={css.offeringHeader}>
            <h4>
              <FormattedMessage id="CreativeMediaFormFields.sectionHeading" />
            </h4>
          </div>
        </div>
        <div className={css.formRow}>
          <div className={css.formFld}>
            <TitleInputField label="Type" id="type" fieldName="title" />
          </div>
          <div className={css.formFld}>
            <PriceInputField formId={formId} />
          </div>
        </div>
      </>
    );
  }

  const {
    needsAddress,
    needsImages,
    needsDescription,
    needsScript,
    needsDateTimePicker,
    dateLabel,
    imagesLabel,
    descriptionLabel,
    descriptionPlaceholder = '',
    scriptLabel,
    scriptPlaceholder = '',
  } = config;

  return (
    <>
      <div className={css.offeringBlock}>
        <div className={css.offeringHeader}>
          <h4>
            <FormattedMessage id="CreativeMediaFormFields.sectionHeading" />
          </h4>
        </div>
      </div>

      <div className={css.formRow}>
        <div className={css.formFld}>
          {/* Title (Type) */}
          <TitleInputField label="Title" id="title" fieldName="title" />
        </div>
        <div className={css.formFld}>
          {/* Price */}
          <PriceInputField formId={formId} />
        </div>
      </div>

      {/* Date Needed */}
      {needsDateTimePicker ? (
        // You'll need a calendar with time slots
        <BookingTimeFormField listing={listing} />
      ) : (
        <DateNeededInputField
          label={dateLabel || `${selectedTitle} is needed by`}
          placeholder="Select a date"
          fieldName="dateNeeded"
        />
      )}
      {/* Shipping Address - Only for Product Testing */}
      {needsAddress && (
        <ShippingAddressFormFields
          fieldName="shippingAddress"
          label="Shipping Address (Where to send the product)"
          required
        />
      )}
      {/* Upload Images */}
      {needsImages && (
        <UploadImagesField
          fieldName="uploadedImages"
          label={imagesLabel || `Upload images for ${selectedTitle.toLowerCase()}`}
        />
      )}

      {/* Main Description / Request */}
      {needsDescription && (
        <PersonalizationFormField
          label={descriptionLabel || 'Describe your request'}
          placeholder={descriptionPlaceholder || 'Provide as much detail as possible...'}
          fieldName="personalizationText"
        />
      )}

      {/* Audio Script - For video/audio with spoken content */}
      {needsScript && (
        <PersonalizationFormField
          label={scriptLabel || 'Exact Script / Voiceover Text'}
          placeholder={
            scriptPlaceholder || 'Type the full script exactly as it should be spoken...'
          }
          fieldName="audioScript"
        />
      )}

      {/* Optional DM Instruction Note */}
      {[
        'Video Creation with Audio',
        'Unboxing Video',
        'How-To Video',
        'Voiceover Recording',
        'Podcast Clip',
        'Behind-the-Scenes Video',
        'Fitness Demonstration Video',
        'Audio Creation',
        'Photo Creation',
        'Product Testing',
      ].includes(selectedTitle) && (
        <div className={css.infoNote}>
          <p>
            <strong>
              <FormattedMessage id="CreativeMediaFormFields.dmInstructionNotePrefix" />
            </strong>{' '}
            <FormattedMessage id="CreativeMediaFormFields.dmInstructionNoteText" />
          </p>
        </div>
      )}
    </>
  );
};

export default CreativeMediaFormFields;
